/**
 * useCurrentUser — gate app rendering on the Convex users row existing.
 *
 * Verbatim from docs.convex.dev/auth/database-auth § "Waiting for
 * current user to be stored". Solves the race between Clerk sign-in
 * resolving and the Clerk → /clerk-users-webhook upserting our
 * convex/users row.
 *
 * Returns isLoading=true while EITHER:
 *   - Clerk auth is still resolving, OR
 *   - Clerk auth has resolved BUT the users row hasn't landed yet
 *     (webhook delivery is eventually consistent, usually <1s).
 *
 * Returns isAuthenticated=true only when Clerk says signed in AND the
 * convex/users row exists. Use this in any gate that needs to read
 * the row (or any descendant that does).
 */
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

export function useCurrentUser(): {
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const { isLoading: clerkLoading, isAuthenticated: clerkAuthed } =
    useConvexAuth();
  const user = useQuery(api.users.current);
  return {
    isLoading: clerkLoading || (clerkAuthed && user === undefined),
    isAuthenticated: clerkAuthed && user !== null && user !== undefined,
  };
}
